import { MessageSquareIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/message-square-icon';
import type { ButtonProps } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/button';
import { Button } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/button';
import { useTiptapEditor } from '@examdojo/core/tiptap-react-ui-components/hooks/use-tiptap-editor';
import type { Editor } from '@tiptap/react';
import { useCallback, useEffect, useState } from 'react';

// Local type declarations for popover commands to avoid circular dependency with core-tiptap-popover
interface PopoverAttributes {
  id?: string;
  htmlContent: string;
  linkUrl?: string;
}

export interface PopoverButtonProps extends Omit<ButtonProps, 'type'> {
  /**
   * The Tiptap editor instance.
   */
  editor?: Editor | null;
  /**
   * Whether to hide the button when unavailable.
   * @default false
   */
  hideWhenUnavailable?: boolean;
}

function canSetPopover(editor: Editor | null): boolean {
  if (!editor) {
    return false;
  }

  // Check if the command exists before calling it
  const can = editor.can() as unknown as Record<string, unknown>;
  const setPopoverFn = can['setPopover'] as ((attrs: PopoverAttributes) => boolean) | undefined;
  return typeof setPopoverFn === 'function' && setPopoverFn({ htmlContent: '' });
}

function shouldShowPopoverButton(params: { editor: Editor | null; hideWhenUnavailable: boolean }): boolean {
  const { editor, hideWhenUnavailable } = params;

  if (!editor) {
    return false;
  }

  if (hideWhenUnavailable) {
    return canSetPopover(editor);
  }

  return Boolean(editor?.isEditable);
}

export function PopoverButton({ editor: providedEditor, hideWhenUnavailable = false, ...props }: PopoverButtonProps) {
  const { editor } = useTiptapEditor(providedEditor);
  const [show, setShow] = useState(false);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!editor) return;

    const handleUpdate = () => {
      setShow(
        shouldShowPopoverButton({
          editor,
          hideWhenUnavailable,
        }),
      );
      setIsActive(editor.isActive('popover'));
    };

    handleUpdate();

    editor.on('selectionUpdate', handleUpdate);
    editor.on('transaction', handleUpdate);

    return () => {
      editor.off('selectionUpdate', handleUpdate);
      editor.off('transaction', handleUpdate);
    };
  }, [editor, hideWhenUnavailable]);

  const handleClick = useCallback(() => {
    if (!editor) return;

    // Use dynamic command access to avoid circular dependency with core-tiptap-popover
    const chain = editor.chain().focus() as unknown as Record<string, unknown>;

    if (isActive) {
      // Remove popover mark
      const unsetPopover = chain['unsetPopover'] as (() => { run: () => void }) | undefined;
      unsetPopover?.()?.run();
    } else {
      // Add popover mark with default content
      const setPopover = chain['setPopover'] as ((attrs: PopoverAttributes) => { run: () => void }) | undefined;
      setPopover?.({
        htmlContent: '<p></p>',
        linkUrl: '',
      })?.run();
    }
  }, [editor, isActive]);

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <Button
      type="button"
      data-style="ghost"
      data-state={isActive ? 'active' : 'inactive'}
      role="button"
      tabIndex={-1}
      tooltip="Add popover"
      onClick={handleClick}
      {...props}
    >
      <MessageSquareIcon className="tiptap-button-icon" />
    </Button>
  );
}
